import { useCallback, useEffect, useMemo, useState, type ReactNode, type FormEvent, type ChangeEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from './_mdmKit.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  GSTIN_PATTERN as GST_RE,
  PAN_PATTERN as PAN_RE,
} from '../../../../shared/contracts/backend-enums.js';
import {
  listStates,
  listDistricts,
  listTerritories,
  listCitiesByDistrict,
  type StateRow,
  type DistrictRow,
  type TerritoryRow,
  type CityRow,
} from '../../api/orgApi.js';

// M05 §7/§8 (T059) — Customer / Dealer Master. Full CRUD over the existing
// backend `@Controller('v1/master/customers')`:
//   GET  /master/customers?q&status&dealerType&territoryCode&page&limit → { data, total }
//   POST /master/customers (CreateCustomerDto) · PUT /master/customers/:id
//   PATCH /master/customers/:id/deactivate
// Client-side §24 validation mirrors the backend regexes (mobile/PIN/GST/PAN/email).

interface Customer {
  readonly customerId: string;
  readonly dealerCode: string;
  readonly companyName: string;
  readonly contactPerson: string;
  readonly mobileNumber: string;
  readonly email: string | null;
  readonly city: string;
  readonly dealerType: string;
  readonly territoryCode: string;
  readonly recordStatusCode: string;
}

const DEALER_TYPES = ['DEALER', 'RETAILER', 'DISTRIBUTOR'] as const;
const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;

const MOBILE_RE = /^[6-9][0-9]{9}$/;
const PIN_RE = /^[0-9]{6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  dealerCode: '', companyName: '', dealerFirmName: '', contactPerson: '', mobileNumber: '',
  email: '', address: '', city: '', districtCode: '', stateCode: '', pinCode: '',
  latitude: '', longitude: '',
  dealerType: 'DEALER', territoryCode: '', zoneCode: '', regionCode: '', gstNumber: '', panNumber: '',
};
type Form = typeof EMPTY;

const PAGE_SIZE = 20;

export function CustomerMasterPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Customer[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dealerFilter, setDealerFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The record stores codes, so the pickers keep the ids alongside only to
  // drive the cascade.
  const [states, setStates] = useState<readonly StateRow[]>([]);
  const [districts, setDistricts] = useState<readonly DistrictRow[]>([]);
  const [cities, setCities] = useState<readonly CityRow[]>([]);
  const [territories, setTerritories] = useState<readonly TerritoryRow[]>([]);
  const [stateId, setStateId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [cityId, setCityId] = useState('');
  // Apart from `formError`, which opening the form clears: an empty cascade
  // must say why for as long as it stays empty.
  const [geoError, setGeoError] = useState<string | null>(null);
  const geoFailed = (e: unknown) => setGeoError((e as Error).message);

  useEffect(() => {
    let alive = true;
    listStates(token)
      .then((r) => { if (alive) setStates(r.data); })
      .catch((e) => { if (alive) { setStates([]); geoFailed(e); } });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    if (!stateId) { setDistricts([]); return; }
    listDistricts(token, stateId)
      .then((r) => { if (alive) setDistricts(r.data); })
      .catch((e) => { if (alive) { setDistricts([]); geoFailed(e); } });
    return () => { alive = false; };
  }, [token, stateId]);

  useEffect(() => {
    let alive = true;
    if (!districtId) { setCities([]); setTerritories([]); return; }
    listCitiesByDistrict(token, districtId)
      .then((r) => { if (alive) setCities(r); })
      .catch((e) => { if (alive) { setCities([]); geoFailed(e); } });
    listTerritories(token, { districtId })
      .then((r) => { if (alive) setTerritories(r.data); })
      .catch((e) => { if (alive) { setTerritories([]); geoFailed(e); } });
    return () => { alive = false; };
  }, [token, districtId]);

  const onStateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setStateId(id);
    setDistrictId('');
    setCityId('');
    const st = states.find((x) => x.stateId === id);
    setForm((f) => ({
      ...f, stateCode: st?.stateCode ?? '', districtCode: '', city: '', pinCode: '', territoryCode: '',
    }));
  };

  const onDistrictChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setDistrictId(id);
    setCityId('');
    const d = districts.find((x) => x.districtId === id);
    setForm((f) => ({
      ...f, districtCode: d?.districtCode ?? '', city: '', pinCode: '', territoryCode: '',
    }));
  };

  const onCityChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setCityId(id);
    const c = cities.find((x) => x.cityId === id);
    setForm((f) => ({
      ...f, city: c?.cityName ?? '', pinCode: c?.pincode ?? f.pinCode,
    }));
  };

  // Editing loads stored codes, so the cascade is walked back to ids to make
  // the existing values visible in the pickers.
  useEffect(() => {
    if (!form.stateCode || stateId || states.length === 0) return;
    const st = states.find((x) => x.stateCode === form.stateCode);
    if (st) setStateId(st.stateId);
  }, [form.stateCode, stateId, states]);

  useEffect(() => {
    if (!form.districtCode || districtId || districts.length === 0) return;
    const d = districts.find((x) => x.districtCode === form.districtCode);
    if (d) setDistrictId(d.districtId);
  }, [form.districtCode, districtId, districts]);

  useEffect(() => {
    if (!form.city || cityId || cities.length === 0) return;
    const c = cities.find((x) => x.cityName === form.city);
    if (c) setCityId(c.cityId);
  }, [form.city, cityId, cities]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (q.trim()) params.set('q', q.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (dealerFilter) params.set('dealerType', dealerFilter);
      const r = await apiRequest<{ data: Customer[]; total: number }>(
        `/master/customers?${params.toString()}`, { token },
      );
      setRows(r.data ?? []);
      setTotal(r.total ?? 0);
      setError(null);
    } catch (e) { setError((e as Error).message); setRows([]); }
  }, [token, page, q, statusFilter, dealerFilter]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const beginCreate = () => { setForm(EMPTY); setEditingId(null); setFormError(null); setShowForm(true); };
  const beginEdit = useCallback(async (c: Customer) => {
    // Seed with the summary row, then fetch the full record so every field
    // (address/PIN/GST/… not present in the list) is populated for the PUT.
    setForm({
      ...EMPTY,
      dealerCode: c.dealerCode, companyName: c.companyName, contactPerson: c.contactPerson,
      mobileNumber: c.mobileNumber, email: c.email ?? '', city: c.city,
      dealerType: c.dealerType, territoryCode: c.territoryCode,
    });
    setEditingId(c.customerId); setFormError(null); setShowForm(true);
    try {
      const full = await apiRequest<Record<string, string | null>>(
        `/master/customers/${encodeURIComponent(c.customerId)}`, { token },
      );
      setForm((f) => ({
        ...f,
        companyName: (full.companyName as string) ?? f.companyName,
        dealerFirmName: (full.dealerFirmName as string) ?? '',
        contactPerson: (full.contactPerson as string) ?? f.contactPerson,
        mobileNumber: (full.mobileNumber as string) ?? f.mobileNumber,
        email: (full.email as string) ?? '',
        address: (full.address as string) ?? '',
        city: (full.city as string) ?? f.city,
        districtCode: (full.districtCode as string) ?? '',
        stateCode: (full.stateCode as string) ?? '',
        pinCode: (full.pinCode as string) ?? '',
        // A dealer that has never been positioned comes back null, which must
        // read as an empty box rather than as the string "null".
        latitude: full.latitude == null ? '' : String(full.latitude),
        longitude: full.longitude == null ? '' : String(full.longitude),
        territoryCode: (full.territoryCode as string) ?? f.territoryCode,
        zoneCode: (full.zoneCode as string) ?? '',
        regionCode: (full.regionCode as string) ?? '',
        gstNumber: (full.gstNumber as string) ?? '',
        panNumber: (full.panNumber as string) ?? '',
      }));
    } catch { /* keep summary values if the detail fetch fails */ }
  }, [token]);

  const clientValidate = (): string | null => {
    if (!MOBILE_RE.test(form.mobileNumber)) return t('mdm.common.invalidMobile');
    if (!PIN_RE.test(form.pinCode)) return t('mdm.customer.invalidPin');
    if (form.email && !EMAIL_RE.test(form.email)) return t('mdm.common.invalidEmail');
    if (form.gstNumber && !GST_RE.test(form.gstNumber)) return t('mdm.customer.invalidGst');
    if (form.panNumber && !PAN_RE.test(form.panNumber)) return t('mdm.customer.invalidPan');
    return coordinateError();
  };

  /**
   * The same rule the database enforces (t059 mirrors t613_coordinates_chk):
   * both or neither, each within the range it is measured in. Checked here too
   * so the dealer is told which box is wrong instead of being handed a
   * constraint violation.
   */
  const coordinateError = (): string | null => {
    const lat = form.latitude.trim();
    const lng = form.longitude.trim();
    if (!lat && !lng) return null;
    if (!lat || !lng) return t('mdm.customer.coordinatesPaired');
    const la = Number(lat);
    const lo = Number(lng);
    if (!Number.isFinite(la) || la < -90 || la > 90) return t('mdm.customer.invalidLatitude');
    if (!Number.isFinite(lo) || lo < -180 || lo > 180) return t('mdm.customer.invalidLongitude');
    return null;
  };

  /**
   * null clears a position that was recorded wrongly; undefined is never sent,
   * because a field the dealer left alone must not be silently rewritten.
   */
  const coordinate = (raw: string): number | null =>
    raw.trim() === '' ? null : Number(raw.trim());

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const v = clientValidate();
    if (v) { setFormError(v); return; }
    setSaving(true); setFormError(null);
    const trimmed = (x: string) => (x.trim() ? x.trim() : undefined);
    try {
      if (editingId) {
        await apiRequest(`/master/customers/${encodeURIComponent(editingId)}`, {
          method: 'PUT', token,
          body: {
            companyName: form.companyName.trim(), contactPerson: form.contactPerson.trim(),
            mobileNumber: form.mobileNumber.trim(), email: trimmed(form.email),
            address: trimmed(form.address), city: trimmed(form.city),
            districtCode: trimmed(form.districtCode), stateCode: trimmed(form.stateCode),
            pinCode: form.pinCode.trim(), territoryCode: trimmed(form.territoryCode),
            latitude: coordinate(form.latitude), longitude: coordinate(form.longitude),
            zoneCode: trimmed(form.zoneCode), regionCode: trimmed(form.regionCode),
            gstNumber: trimmed(form.gstNumber), panNumber: trimmed(form.panNumber),
          },
        });
      } else {
        await apiRequest('/master/customers', {
          method: 'POST', token,
          body: {
            dealerCode: form.dealerCode.trim(), companyName: form.companyName.trim(),
            dealerFirmName: trimmed(form.dealerFirmName), contactPerson: form.contactPerson.trim(),
            mobileNumber: form.mobileNumber.trim(), email: trimmed(form.email),
            address: form.address.trim(), city: form.city.trim(),
            districtCode: form.districtCode.trim(), stateCode: form.stateCode.trim(),
            pinCode: form.pinCode.trim(), dealerType: form.dealerType,
            latitude: coordinate(form.latitude) ?? undefined,
            longitude: coordinate(form.longitude) ?? undefined,
            territoryCode: form.territoryCode.trim(), zoneCode: trimmed(form.zoneCode),
            regionCode: trimmed(form.regionCode), gstNumber: trimmed(form.gstNumber),
            panNumber: trimmed(form.panNumber),
          },
        });
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [editingId, form, token, load]);

  const onDeactivate = useCallback(async (c: Customer) => {
    setError(null);
    try {
      await apiRequest(`/master/customers/${encodeURIComponent(c.customerId)}/deactivate`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <MdmShell
      label="customer-master"
      title={t('mdm.customer.title')}
      description={t('mdm.customer.desc')}
      actions={<Button type="button" variant="primary" data-testid="new-customer" onClick={beginCreate}>{t('mdm.customer.newBtn')}</Button>}
    >
      <form aria-label="customer-filters" onSubmit={(e) => { e.preventDefault(); setPage(1); void load(); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="cust-q" label={t('mdm.customer.searchLabel')}>
          <input id="cust-q" value={q} onChange={(e) => setQ(e.target.value)} style={s.input} placeholder={t('mdm.customer.searchPh')} />
        </Field>
        <Field id="cust-status" label={t('mdm.col.status')}>
          <select id="cust-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Field id="cust-dealer" label={t('mdm.customer.dealerTypeLabel')}>
          <select id="cust-dealer" value={dealerFilter} onChange={(e) => { setDealerFilter(e.target.value); setPage(1); }} style={s.input}>
            <option value="">{t('mdm.common.all')}</option>
            {DEALER_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Button type="submit" variant="secondary" data-testid="apply-filters">{t('mdm.common.search')}</Button>
      </form>

      {showForm && (
        <form onSubmit={onSubmit} aria-label={editingId ? 'edit-customer-form' : 'create-customer-form'} style={s.card}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{editingId ? t('mdm.customer.editTitle') : t('mdm.customer.newTitle')}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field id="f-dealerCode" label={t('mdm.customer.dealerCode')}>
              <input id="f-dealerCode" value={form.dealerCode} onChange={set('dealerCode')} required disabled={!!editingId} style={s.input} />
            </Field>
            <Field id="f-companyName" label={t('mdm.customer.companyName')}>
              <input id="f-companyName" value={form.companyName} onChange={set('companyName')} required style={s.input} />
            </Field>
            <Field id="f-dealerFirmName" label={t('mdm.customer.firmName')}>
              <input id="f-dealerFirmName" value={form.dealerFirmName} onChange={set('dealerFirmName')} style={s.input} />
            </Field>
            <Field id="f-contactPerson" label={t('mdm.customer.contactPerson')}>
              <input id="f-contactPerson" value={form.contactPerson} onChange={set('contactPerson')} required style={s.input} />
            </Field>
            <Field id="f-mobileNumber" label={t('mdm.customer.mobile')} hint={t('mdm.common.mobileHint')}>
              <input id="f-mobileNumber" value={form.mobileNumber} onChange={set('mobileNumber')} required style={s.input} />
            </Field>
            <Field id="f-email" label={t('mdm.customer.email')}>
              <input id="f-email" value={form.email} onChange={set('email')} style={s.input} />
            </Field>
            <Field id="f-address" label={t('mdm.customer.address')}>
              <input id="f-address" value={form.address} onChange={set('address')} required={!editingId} style={s.input} />
            </Field>
            <Field id="f-stateCode" label={t('mdm.customer.state')}>
              <select id="f-stateCode" value={stateId} onChange={onStateChange} required={!editingId} style={s.input}>
                <option value="">— Select —</option>
                {states.map((x) => (
                  <option key={x.stateId} value={x.stateId}>{x.stateCode} — {x.stateName}</option>
                ))}
              </select>
            </Field>
            <Field id="f-districtCode" label={t('mdm.customer.district')}>
              <select id="f-districtCode" value={districtId} onChange={onDistrictChange} required={!editingId} disabled={!stateId} style={s.input}>
                <option value="">{stateId ? '— Select —' : '— Select a state first —'}</option>
                {districts.map((x) => (
                  <option key={x.districtId} value={x.districtId}>{x.districtCode} — {x.districtName}</option>
                ))}
              </select>
            </Field>
            <Field id="f-city" label={t('mdm.customer.city')}>
              <select id="f-city" value={cityId} onChange={onCityChange} required={!editingId} disabled={!districtId} style={s.input}>
                <option value="">{districtId ? '— Select —' : '— Select a district first —'}</option>
                {cities.map((x) => (
                  <option key={x.cityId} value={x.cityId}>
                    {x.cityName}{x.pincode ? ` — ${x.pincode}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="f-pinCode" label={t('mdm.customer.pin')} hint={t('mdm.customer.pinHint')}>
              {/* Filled from the chosen city, but still editable because not every city row carries a pincode. */}
              <input id="f-pinCode" value={form.pinCode} onChange={set('pinCode')} required style={s.input} />
            </Field>
            {/*
              Where the dealer actually is. The backend has accepted these two
              since M05 shipped and this screen never asked for them, so every
              dealer carried NULL and the job wizard asked a sales officer to
              stand somewhere instead — which recorded his office, or 0,0.
              Optional: a dealer whose position is not known says so.
            */}
            <Field id="f-latitude" label={t('mdm.customer.latitude')} hint={t('mdm.customer.coordinatesHint')}>
              <input
                id="f-latitude"
                type="number"
                step="any"
                min={-90}
                max={90}
                value={form.latitude}
                onChange={set('latitude')}
                style={s.input}
              />
            </Field>
            <Field id="f-longitude" label={t('mdm.customer.longitude')}>
              <input
                id="f-longitude"
                type="number"
                step="any"
                min={-180}
                max={180}
                value={form.longitude}
                onChange={set('longitude')}
                style={s.input}
              />
            </Field>
            <Field id="f-dealerType" label={t('mdm.customer.dealerTypeReq')}>
              <select id="f-dealerType" value={form.dealerType} onChange={set('dealerType')} disabled={!!editingId} style={s.input}>
                {DEALER_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field id="f-territoryCode" label={t('mdm.customer.territory')}>
              <select
                id="f-territoryCode"
                value={form.territoryCode}
                onChange={set('territoryCode')}
                required
                style={s.input}
              >
                <option value="">
                  {districtId ? '— Select —' : '— Select a district first —'}
                </option>
                {/* The stored territory may not be in this district's list; keep
                    it selectable so editing cannot silently drop it. */}
                {form.territoryCode
                  && !territories.some((x) => x.territoryCode === form.territoryCode) && (
                  <option value={form.territoryCode}>{form.territoryCode}</option>
                )}
                {territories.map((x) => (
                  <option key={x.territoryId} value={x.territoryCode}>
                    {x.territoryCode} — {x.territoryName}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="f-gstNumber" label={t('mdm.customer.gst')}>
              <input id="f-gstNumber" value={form.gstNumber} onChange={set('gstNumber')} style={s.input} />
            </Field>
            <Field id="f-panNumber" label={t('mdm.customer.pan')}>
              <input id="f-panNumber" value={form.panNumber} onChange={set('panNumber')} style={s.input} />
            </Field>
          </div>
          <ErrorAlert message={geoError} />
          <ErrorAlert message={formError} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" variant="primary" data-testid="save-customer" disabled={saving}>
              {saving ? t('mdm.common.saving') : editingId ? t('mdm.common.saveChanges') : t('mdm.customer.createBtn')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>{t('mdm.common.cancel')}</Button>
          </div>
        </form>
      )}

      <ErrorAlert message={error} />
      {!rows ? (
        <p>{t('mdm.common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="customers-empty" style={{ color: s.muted }}>{t('mdm.customer.empty')}</p>
      ) : (
        <>
          <DataTable<Customer>
            ariaLabel="customers-table"
            rowKey={(c) => c.customerId}
            rows={rows}
            columns={[
              { key: 'dealerCode', header: t('mdm.customer.colDealerCode'), render: (c) => c.dealerCode },
              { key: 'company', header: t('mdm.customer.colCompany'), render: (c) => c.companyName },
              { key: 'contact', header: t('mdm.customer.colContact'), render: (c) => c.contactPerson },
              { key: 'mobile', header: t('mdm.customer.colMobile'), render: (c) => c.mobileNumber },
              { key: 'type', header: t('mdm.customer.colType'), render: (c) => c.dealerType },
              { key: 'territory', header: t('mdm.customer.colTerritory'), render: (c) => c.territoryCode },
              { key: 'status', header: t('mdm.col.status'), render: (c) => <StatusBadge status={c.recordStatusCode} /> },
              { key: 'actions', header: t('mdm.col.actions'), render: (c) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button type="button" variant="secondary" data-testid={`edit-${c.customerId}`} onClick={() => void beginEdit(c)}>{t('mdm.common.edit')}</Button>
                  {c.recordStatusCode === 'ACTIVE' && (
                    <Button type="button" variant="danger" data-testid={`deactivate-${c.customerId}`} onClick={() => onDeactivate(c)}>{t('mdm.common.deactivate')}</Button>
                  )}
                </div>
              ) },
            ]}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <Button type="button" variant="secondary" disabled={page <= 1} data-testid="prev-page" onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('mdm.common.prev')}</Button>
            <span style={{ fontSize: 13, color: s.muted }} data-testid="page-info">{t('mdm.customer.pageInfo', { page, pages, total })}</span>
            <Button type="button" variant="secondary" disabled={page >= pages} data-testid="next-page" onClick={() => setPage((p) => Math.min(pages, p + 1))}>{t('mdm.common.next')}</Button>
          </div>
        </>
      )}
    </MdmShell>
  );
}
